/**
 * Typed stub for wizard steps the feature agents fill. The slice is already bound to react-hook-form
 * (`useFormContext<DraftConfigInput>()`), autosaved and validated; a feature agent replaces the element in
 * `src/routes/index.tsx` (see docs/modules/console.md "Wizard slice contract").
 */
import { useFormContext, useWatch } from 'react-hook-form';
import { FormErrorSummary } from '../../../components/FormField';
import { useWizard } from './WizardFrame';
import { WIZARD_STEPS, stepForPath, type DraftConfigInput, type WizardStepId } from './steps';

export function StepStub({ step }: { step: WizardStepId }) {
  const def = WIZARD_STEPS.find((s) => s.id === step)!;
  const { control } = useFormContext<DraftConfigInput>();
  const values = useWatch({ control });
  const { issues, goToStep } = useWizard();
  const mine = issues.filter((i) => i.step === step);
  return (
    <div className="wd-stack" data-testid={`stub-${step}`}>
      <div className="wdc-callout">
        <p>
          <strong>{def.label}</strong> is bound to <code>{def.slices.join(', ')}</code> and autosaves; its
          editor lands with its module agent. Until then the slice is shown read-only and every validation
          issue still links to its field.
        </p>
      </div>
      <FormErrorSummary
        issues={mine}
        title={`${mine.length} issue${mine.length === 1 ? '' : 's'} in this slice`}
        onNavigate={(p) => goToStep(stepForPath(p))}
      />
      {def.slices.map((slice) => (
        <details key={slice} open>
          <summary>
            <code>{slice}</code>
          </summary>
          <pre style={{ maxHeight: 360 }}>
            {JSON.stringify((values as Record<string, unknown>)[slice], null, 2)}
          </pre>
        </details>
      ))}
    </div>
  );
}
